import { useCallback, useState } from 'react';
import { api, ApiError, type Confirmation } from './api';

type Pending = {
  action: string;
  token: string;
  retry: (confirm: Confirmation) => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
};

/**
 * Runs an action, and if the account asks for an emailed code, asks for it and
 * runs it again.
 *
 * The first attempt carries no code on purpose: most accounts have this off,
 * and sending everyone through a code they do not need would be friction paid
 * by everybody to protect the few who asked for it. The server decides, and 428
 * is how it says so.
 *
 * Nothing in the cabinet turns it on since the Security page went in #2141.
 * This stays for an account that switched it on while the setting existed,
 * because without it the 428 would be an error with no way past it.
 */
export function useConfirmable() {
  const [pending, setPending] = useState<Pending | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const run = useCallback(
    async <T,>(action: string, attempt: (confirm: Confirmation) => Promise<T>): Promise<T> => {
      try {
        return await attempt(undefined);
      } catch (err) {
        if (!(err instanceof ApiError) || err.status !== 428) {
          throw err;
        }
      }

      const challenge = await api.requestConfirmation(action);
      return new Promise<T>((resolve, reject) => {
        setCode('');
        setError('');
        setPending({
          action,
          token: challenge.confirmation_token,
          retry: attempt as (confirm: Confirmation) => Promise<unknown>,
          resolve: resolve as (value: unknown) => void,
          reject,
        });
      });
    },
    [],
  );

  const submit = async () => {
    if (!pending) return;
    setBusy(true);
    setError('');
    try {
      const result = await pending.retry({ code: code.trim(), token: pending.token });
      pending.resolve(result);
      setPending(null);
    } catch (err) {
      // A wrong code is a person mistyping: stay on the dialog rather than
      // failing the action they asked for.
      setError(
        err instanceof ApiError && err.status === 428
          ? 'That code is not valid. Check the newest email, or cancel and try again.'
          : err instanceof Error
            ? err.message
            : 'That did not work',
      );
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    if (!pending) return;
    pending.reject(new Error('cancelled'));
    setPending(null);
  };

  const dialog = pending ? (
    <div className="modal-backdrop">
      <div className="modal panel">
        <h3>Confirm this action</h3>
        <p className="muted small">
          A code went to your email. It expires in five minutes and works only for this action.
        </p>
        <label htmlFor="confirm-code">Code</label>
        <input
          id="confirm-code"
          autoFocus
          value={code}
          onChange={(event) => setCode(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && code.trim()) void submit();
          }}
        />
        {error ? <p className="error">{error}</p> : null}
        <div className="row" style={{ marginTop: 12 }}>
          <button onClick={submit} disabled={busy || !code.trim()}>
            Confirm
          </button>
          <button className="secondary" onClick={cancel} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { run, dialog };
}
